import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import { DeleteOutlined, EditOutlined, MoreVertOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import { MARK_AS_NOT_RECEIVED_LABEL_MAP } from "../../constants"

import { type GoodsReceipt, GoodsReceiptStatus } from "@/graphql/codegen/graphql"

interface GoodsReceiptRowActionsProps {
  row: CellContext<GoodsReceipt, unknown>["row"]
  status: GoodsReceiptStatus
  onEdit?: (goodsReceipt: GoodsReceipt) => void
  onDelete?: (goodsReceipt: GoodsReceipt) => void
  onMarkAsNotReceived?: (goodsReceipt: GoodsReceipt) => void
}

export const GoodsReceiptRowActions: FC<GoodsReceiptRowActionsProps> = ({
  row,
  status,
  onEdit,
  onDelete,
  onMarkAsNotReceived,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleDropdownOpen = () => setIsDropdownOpen(true)

  const handleDropdownClose = () => setIsDropdownOpen(false)

  const handleEdit = () => {
    handleDropdownClose()
    onEdit?.(row.original)
  }

  const handleDelete = () => {
    handleDropdownClose()
    onDelete?.(row.original)
  }

  const handleMarkAsNotReceived = () => {
    handleDropdownClose()
    onMarkAsNotReceived?.(row.original)
  }

  const markAsNotReceivedLabel = MARK_AS_NOT_RECEIVED_LABEL_MAP[status]
  const shouldShowEdit = [GoodsReceiptStatus.Incoming, GoodsReceiptStatus.SoftReceived].includes(row.original.status)
  const shouldShowMarkAsNotReceived = shouldShowEdit && markAsNotReceivedLabel !== undefined
  const shouldShowDelete = status === GoodsReceiptStatus.Incoming

  if (!shouldShowEdit && !shouldShowMarkAsNotReceived && !shouldShowDelete) {
    return null
  }

  return (
    <Box className='flex items-center justify-center' ref={anchorElRef}>
      <IconButton onClick={handleDropdownOpen} data-test='row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isDropdownOpen}
        onClose={handleDropdownClose}
        data-test='row-actions-menu'
      >
        {shouldShowEdit && (
          <MenuItem data-test='edit-menu-item' onClick={handleEdit}>
            <ListItemIcon>
              <EditOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            </ListItemIcon>
            <ListItemText primary='Edit' />
          </MenuItem>
        )}
        {shouldShowMarkAsNotReceived && (
          <MenuItem data-test='mark-as-not-received-menu-item' onClick={handleMarkAsNotReceived}>
            <ListItemIcon>
              <WarningOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            </ListItemIcon>
            <ListItemText primary={markAsNotReceivedLabel} />
          </MenuItem>
        )}
        {shouldShowDelete && (
          <MenuItem data-test='delete-menu-item' onClick={handleDelete}>
            <ListItemIcon>
              <DeleteOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            </ListItemIcon>
            <ListItemText primary='Delete' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
